import React, { useState, useEffect } from "react";
import Form from "react-bootstrap/Form";
import { Boxes } from "lucide-react";
import { FaRegFolderOpen } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBook,
  faLightbulb,
  faQuestionCircle,
  faCloudUploadAlt,
  faFilePdf,
  faFileWord,
  faCircleCheck,
  faCircleXmark,
} from "@fortawesome/free-solid-svg-icons";
import ProgressBar from "react-bootstrap/ProgressBar";
import Accordion from "react-bootstrap/Accordion";
import { CircularProgress, Typography } from "@mui/material";
import { Card, Container } from "react-bootstrap";
import { useAuth } from "../../Context/AuthContext";
import { useTranslation } from "react-i18next"; // Importer useTranslation

function ModuleProgression({ members, program, isCreator }) {
  const { t } = useTranslation(); // Assigner useTranslation à {t}
  const [selectedMember, setSelectedMember] = useState("");
  const [modulesProgress, setModulesProgress] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userProgress, setUserProgress] = useState(null);
  const { authData } = useAuth();

  const formattedProgram = {
    ...program,
    modules: (program.modules || [])
      .filter((module) => !module.is_deleted && module.is_visible)
      .map((module) => ({
        id: module._id,
        title: module.title,
        sections: (module.sections || [])
          .filter((section) => !section.is_deleted && section.is_visible)
          .map((section) => ({
            id: section._id,
            title: section.title,
            lessons: (section.lessons || [])
              .filter((lesson) => !lesson.is_deleted && lesson.is_visible)
              .map((lesson) => ({
                id: lesson._id,
                title: lesson.title,
                type: lesson.type,
              })),
          })),
      })),
  };

  useEffect(() => {
    if (!isCreator && authData.userId) {
      setSelectedMember(authData.userId);
      fetchModulesProgress(authData.userId);
    }
  }, [isCreator, authData.userId]);

  const fetchModulesProgress = async (userId) => {
    try {
      if (!program._id || !userId) return;

      setLoading(true);

      const progressResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/user-program-progress/all-modules-progress/${program._id}?user=${userId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );

      if (!progressResponse.ok) {
        throw new Error(t("fetch_progress_error"));
      }

      const progressData = await progressResponse.json();
      setModulesProgress(progressData);

      const userProgressResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/user-program-progress/get?program=${program._id}&user=${userId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );

      if (!userProgressResponse.ok) {
        throw new Error(t("fetch_user_progress_error"));
      }

      const userProgressData = await userProgressResponse.json();
      setUserProgress(userProgressData);
    } catch (error) {
      console.error(t("error_fetching_progress"), error);
    } finally {
      setLoading(false);
    }
  };

  const getIconForLessonType = (type) => {
    switch (type) {
      case "Theory":
        return faBook;
      case "Quiz":
        return faQuestionCircle;
      case "Upload":
        return faCloudUploadAlt;
      case "PDF":
        return faFilePdf;
      case "Word":
        return faFileWord;
      default:
        return null;
    }
  };

  const getColorForLessonType = (type) => {
    switch (type) {
      case "Theory":
        return "#0d6efd";
      case "Quiz":
        return "#28a745";
      case "Upload":
        return "#17a2b8";
      case "PDF":
        return "#e60000";
      case "Word":
        return "#1e3799";
      default:
        return "#6c757d";
    }
  };

  const handleMemberSelection = (memberId) => {
    setSelectedMember(memberId);
    fetchModulesProgress(memberId);
  };

  const isLessonCompleted = (lessonId) => {
    if (!userProgress || !userProgress.completedLessons) return false;
    return userProgress.completedLessons.some(
      (id) => id === lessonId || id.toString() === lessonId
    );
  };

  const getModuleProgress = (moduleId) => {
    const moduleData = modulesProgress.find(
      (item) => item.moduleId === moduleId
    );
    return moduleData ? moduleData.progress : 0;
  };

  return (
    <Container className="mt-4 mb-5">
      {isCreator && (
        <Form.Group
          controlId="memberSelect"
          className="d-flex flex-column align-items-center mb-4"
        >
          <Form.Select
            className="w-50"
            value={selectedMember}
            onChange={(e) => handleMemberSelection(e.target.value)}
          >
            <option value="">{t("select_member")}</option>
            {members
              .filter((member) => member.role === "user")
              .map((member, index) => (
                <option key={index} value={member._id}>
                  {member.first_name} {member.last_name} - {member.email}
                </option>
              ))}
          </Form.Select>
        </Form.Group>
      )}

      {loading ? (
        <div className="text-center my-5">
          <Typography variant="body1" className="mb-3">
            {t("loading_progress")}
          </Typography>
          <CircularProgress />
        </div>
      ) : selectedMember ? (
        <Accordion className="mt-4">
          {formattedProgram.modules.map((module, moduleIndex) => {
            const moduleProgress = getModuleProgress(module.id);

            return (
              <Accordion.Item
                key={module.id}
                eventKey={`module-${moduleIndex}`}
              >
                <Accordion.Header>
                  <div className="d-flex align-items-center w-100">
                    <div
                      className="me-3 d-flex align-items-center justify-content-center bg-primary bg-gradient rounded-circle"
                      style={{ width: "50px", height: "50px", padding: "10px" }}
                    >
                      <Boxes size={24} color="#fff" />
                    </div>
                    <div className="flex-grow-1">
                      <h5 className="mb-0">{module.title}</h5>
                      <ProgressBar
                        now={moduleProgress}
                        label={
                          <span style={{ fontSize: "15px", fontWeight: "500" }}>
                            {`${Math.round(moduleProgress)}%`}
                          </span>
                        }
                        className="mt-2"
                        variant="success"
                        style={{ height: "19px" }}
                      />
                    </div>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <Accordion className="ms-4">
                    {module.sections.map((section, sectionIndex) => (
                      <Accordion.Item
                        key={section.id}
                        eventKey={`section-${moduleIndex}-${sectionIndex}`}
                        className="mb-2"
                      >
                        <Accordion.Header>
                          <div className="d-flex align-items-center">
                            <FaRegFolderOpen className="me-3" size={20} />
                            <h6 className="mb-0">{section.title}</h6>
                          </div>
                        </Accordion.Header>
                        <Accordion.Body>
                          <div className="ms-4">
                            {section.lessons.map((lesson, lessonIndex) => {
                              const isCompleted = isLessonCompleted(lesson.id);
                              return (
                                <Card
                                  key={lesson.id}
                                  className="mb-2"
                                  border={isCompleted ? "success" : "light"}
                                >
                                  <Card.Body className="d-flex justify-content-between align-items-center py-2">
                                    <div className="d-flex align-items-center">
                                      <FontAwesomeIcon
                                        icon={getIconForLessonType(lesson.type)}
                                        className="me-3"
                                        style={{
                                          color: getColorForLessonType(
                                            lesson.type
                                          ),
                                        }}
                                      />
                                      <span>{lesson.title}</span>
                                    </div>
                                    <FontAwesomeIcon
                                      icon={
                                        isCompleted
                                          ? faCircleCheck
                                          : faCircleXmark
                                      }
                                      className={
                                        isCompleted
                                          ? "text-success"
                                          : "text-danger"
                                      }
                                      size="lg"
                                    />
                                  </Card.Body>
                                </Card>
                              );
                            })}
                            {section.lessons.length === 0 && (
                              <p className="text-muted text-center fst-italic">
                                {t("no_lessons_in_section")}
                              </p>
                            )}
                          </div>
                        </Accordion.Body>
                      </Accordion.Item>
                    ))}
                    {module.sections.length === 0 && (
                      <p className="text-muted text-center fst-italic">
                        {t("no_sections_in_module")}
                      </p>
                    )}
                  </Accordion>
                </Accordion.Body>
              </Accordion.Item>
            );
          })}
        </Accordion>
      ) : (
        <div className="text-center my-5">
          <Typography variant="body1" className="text-muted">
            {t("select_member_to_view_progress")}
          </Typography>
        </div>
      )}
    </Container>
  );
}

export default ModuleProgression;
